import type {ReactElement} from 'react';
import React, {useContext} from 'react';
import {Text, TextSize} from '@Components/text';
import styles from './publishing-progress-bar.module.scss';
import {DisplayType} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.types';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {useDisplayType, useOrderDetails, usePosterName, usePosterPreviewUrl, usePrintProduct,} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.hooks';
import {MAX_ELAPSED_TIME_LENGTH, MIN_SECONDS_TO_SHOW_EMAIL_OPTION} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel-library';
import {ModalDetails} from '@Components/modal/modal-context';

export function PublishingProgressBar(): ReactElement<any> {
  const orderDetails = useOrderDetails();
  const premiumProductDataForPrint = usePrintProduct();
  const displayType = useDisplayType();
  const isModal = displayType === DisplayType.MODAL;
  const modalDetails = useContext(ModalDetails);
  const inMobile = modalDetails?.isMobileModal;
  const isDropDownOrMobile = !isModal || inMobile;
  const posterName = usePosterName();
  const posterPreviewUrl = usePosterPreviewUrl();

  const getTextForLoader = (): string => {
    if (orderDetails.error !== '') {
      return window.i18next.t('pmwjs_publishing_loader_error_text');
    } else if (orderDetails.timeElapsedInSeconds > MIN_SECONDS_TO_SHOW_EMAIL_OPTION) {
      return window.i18next.t('pmwjs_publishing_loader_estimated_time_text', {
        estimatedTimeInSeconds: orderDetails.estimatedTimeInSeconds,
        seconds: orderDetails.estimatedTimeInSeconds.length > MAX_ELAPSED_TIME_LENGTH ? '' : window.i18next.t('pmwjs_seconds'),
      });
    }
    return window.i18next.t('pmwjs_publishing_loader_default_text');
  };

  const getPosterName = (): string => {
    if (premiumProductDataForPrint.posterName !== '') {
      return premiumProductDataForPrint.posterName;
    }
    return posterName;
  };
  const isInErrorState = (): boolean => {
    return orderDetails.error !== '';
  };

  const getImageStyles = (): string => {
    const aspectRatio: number = (premiumProductDataForPrint?.width || 0) / (premiumProductDataForPrint?.height || 0);

    switch (true) {
      case aspectRatio > 2:
        return styles.imageStyleHorizontal;

      case aspectRatio < 0.5:
        return styles.imageStyleVertical;

      case aspectRatio === 1:
        return styles.imageStyleSquare;

      default:
        return styles.imageStyleDefault;
    }
  };
  const getPosterThumbnail = (): ReactElement<any> => {
    const isError = isInErrorState();

    return (
      <div className={styles.thumbnailWrapper}>
        <div className={`${isError ? 'bg-danger-20' : styles.thumbnailBackgroundDefault} flex-center _overflow-hidden radius-round ${styles.thumbnailStyle}`}>
          <img className={getImageStyles()} src={posterPreviewUrl} alt="" />
        </div>
        <div className={`${isError ? styles.svgFirstCircleBackgroundError : styles.svgFirstCircleBackgroundDefault} ${styles.svgStyleFirstCircle} radius-round`}>
          <div className={`bg-neutral-0 ${isError ? `radius-round ${styles.svgSecondCircleBorderError}` : styles.svgSecondCircleBorderDefault} _overflow-hidden radius-round`}>
            {isError ? (
              <svg
                className={`flex-center spacing-m-4 _overflow-visible ${styles.iconEnlarge}`}
                xmlns="http://www.w3.org/2000/svg"
                width="32"
                height="32"
                viewBox="0 0 32 32"
                fill="none"
              >
                <path
                  d="M27.3459 25.3513H4.52309L15.9341 5.5861L27.3459 25.3513ZM17.4291 4.7238C16.7652 3.57319 15.1038 3.57319 14.44 4.7238L3.02898 24.489C2.36512 25.6396 3.19538 27.0777 4.52398 27.0777H27.3468C28.6754 27.0777 29.5057 25.6396 28.8418 24.489L17.4309 4.7238H17.4291Z"
                  fill="#9C002F"
                />
                <path
                  d="M15.935 11.6274C16.4119 11.6274 16.7982 12.0137 16.7982 12.4906V17.0931C16.7982 17.5701 16.4119 17.9563 15.935 17.9563C15.458 17.9563 15.0718 17.5701 15.0718 17.0931V12.4906C15.0718 12.0137 15.458 11.6274 15.935 11.6274Z"
                  fill="#9C002F"
                />
                <path
                  d="M15.0718 21.6947C15.0718 21.2178 15.458 20.8315 15.935 20.8315H15.9465C16.4226 20.8315 16.8097 21.2178 16.8097 21.6947C16.8097 22.1717 16.4235 22.5579 15.9465 22.5579H15.935C15.458 22.5579 15.0718 22.1717 15.0718 21.6947Z"
                  fill="#9C002F"
                />
              </svg>
            ) : (
              <div className="publish-icon-looped-animation flex-center">
                <Icon icon="icon-publish-arrow content-primary spacing-p-4" size={IconSize.SIZE_ICON_32} />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const getPanelContent = (): ReactElement<any> => {
    const isError = isInErrorState();
    return (
      <div className={`flex-v-row ${isDropDownOrMobile ? `_full-width spacing-p-4 ` : `spacing-p-t-7 spacing-p-b-7 spacing-p-l-12 spacing-p-r-12`}     `}>
        <div className={`flex-v-row ${isDropDownOrMobile ? 'spacing-m-b-4' : 'spacing-m-b-10'}   ${styles.textGap}`}>
          <div className="flex-center spacing-p-t-5 spacing-m-b-4">{getPosterThumbnail()}</div>
          <Text val={getTextForLoader()} className={`flex-center ${isError ? 'color-danger-100' : 'content-body'}`} size={isDropDownOrMobile ? TextSize.XSMALL : TextSize.MEDIUM} />
        </div>
        <div className={`${isError ? '_hidden' : ''} flex-v-row`}>
          <div className={`flex-row-justify-between spacing-m-b-2`}>
            <Text className="body-xs-bold content-body" val={getPosterName()} />
            <Text className="body-xs-bold content-primary" val={`${orderDetails.progressPercentage}%`} />
          </div>
          <div className={isDropDownOrMobile ? 'spacing-b-2' : 'spacing-p-b-4'}>
            <div className={styles.progressBarContainer}>
              <span className={styles.progressBarLoader} style={{width: `${orderDetails.progressPercentage}%`}} />
            </div>
          </div>
        </div>
      </div>
    );
  };

  return getPanelContent();
}
